import {
  type AcademicTerm,
  type AssignmentCopyRequest,
  type AssignmentFolder,
  type AssignmentFolderAction,
  assignmentFolderId,
  assignmentFolders,
  type Classroom,
  compareStudentNumbers,
  dateInTerm,
  type HomeworkAssignment,
  type HomeworkRecord,
  type HomeworkStudentRecord,
  type Student,
  studentNumberLabel,
} from "@classroom/domain";
import { type Ref, useEffect, useImperativeHandle, useRef, useState } from "react";
import AssignmentBatchDialog from "./AssignmentBatchDialog";
import AssignmentCopyDialog from "./AssignmentCopyDialog";
import "./teacher-workflows.css";
import AssignmentFolderDialog from "./AssignmentFolderDialog";
import AssignmentFolderIcon from "./AssignmentFolderIcon";
import AutosaveStatus from "./AutosaveStatus";
import GradeCategory from "./GradeCategory";
import { i18n, interfaceLocale, useTranslation } from "./i18n";
import { useDialogFocus } from "./lib/accessibility";
import { assignmentRecordLabels } from "./lib/assignmentRecordLabels";
import { useAutosavedDraft } from "./lib/useAutosavedDraft";
import { navigationId, useNavigationState } from "./lib/workspaceNavigation";
import StudentPagination, { useStudentCardPageSize, useStudentPagination } from "./StudentPagination";

export type AssignmentNavigation = {
  goBack: (onPreviousView?: () => void) => boolean;
  openAssignment?: (id: string) => void;
};

type Props = {
  readOnly?: boolean;
  classroom: Classroom;
  classes: Classroom[];
  dateRange?: { startDate?: string; endDate?: string };
  onCopy: (request: AssignmentCopyRequest) => Promise<unknown>;
  onFolderAction: (action: AssignmentFolderAction) => Promise<unknown>;
  terms: AcademicTerm[];
  recordTerm?: AcademicTerm;
  onSelectTerm?: (id: string) => void;
  defaultRecordDate?: string;
  workspaceId: string;
  classId: string;
  students: Student[];
  assignments: HomeworkAssignment[];
  records: HomeworkRecord[];
  onSaveAssignment: (item: HomeworkAssignment) => Promise<unknown>;
  onSaveRecord: (item: HomeworkRecord) => Promise<unknown>;
  onDeleteAssignment: (item: HomeworkAssignment, confirmationToken: string) => Promise<unknown>;
  deletionToken: (item: HomeworkAssignment) => string;
  navigationRef?: Ref<AssignmentNavigation>;
};

const optionalNavigationId = (value: unknown): value is string | undefined =>
  value === undefined || navigationId(value);

export default function AssignmentRecords(props: Props) {
  const { t: copy } = useTranslation("assignmentRecords");
  const navigationKey = `assignmentRecords:${props.classId}`;
  const [selected, setSelected] = useNavigationState<string | undefined>(
    `${navigationKey}:selected`,
    undefined,
    optionalNavigationId,
  );
  const [copying, setCopying] = useState<HomeworkAssignment>();
  const [notice, setNotice] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<HomeworkAssignment>();
  const [viewTermId, setViewTermId] = useNavigationState<string | undefined>(
    `${navigationKey}:term`,
    undefined,
    optionalNavigationId,
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [savedCategory, setCategory] = useNavigationState<string | undefined>(
    `${navigationKey}:category`,
    undefined,
    optionalNavigationId,
  );
  const [folderDialog, setFolderDialog] = useState<{ folder?: AssignmentFolder }>();
  const [formDirty, setFormDirty] = useState(false);
  const [deleting, setDeleting] = useState<{ item: HomeworkAssignment; token: string }>();
  const closeForm = () => {
    if (saving || (formDirty && !window.confirm(copy("discardConfirm")))) return;
    setCreating(false);
    setEditing(undefined);
    setFormDirty(false);
    setError("");
  };
  const formDialogRef = useDialogFocus(Boolean(creating || editing), closeForm);
  const deleteDialogRef = useDialogFocus(Boolean(deleting), () => {
    if (!saving) setDeleting(undefined);
  });
  const folderCreateButton = useRef<HTMLButtonElement>(null);
  const folders = assignmentFolders(props.classroom);
  const category =
    savedCategory === "uncategorized" || folders.some((folder) => folder.id === savedCategory)
      ? savedCategory
      : undefined;
  const categoryLabels: Record<string, string> = Object.fromEntries([
    ...folders.map((folder) => [folder.id, folder.name] as const),
    ["uncategorized", copy("folders.uncategorized")] as const,
  ]);
  const editorNavigation = useRef<AssignmentNavigation>(null);
  const directEntry = useRef<{ selected?: string; category?: string; viewTermId?: string; query: string }>(undefined);
  const returnToCategories = () => {
    setCategory(undefined);
    setQuery("");
    setCreating(false);
    setEditing(undefined);
    setError("");
  };
  // The shared top bar must leave this nested view before consuming console history.
  useImperativeHandle(props.navigationRef, () => ({
    openAssignment: (id: string) => {
      const item = props.assignments.find((item) => item.id === id && item.classId === props.classId);
      if (!item) return;
      // A dashboard shortcut skips the directory; shared Back must restore its actual source.
      directEntry.current ??= { selected, category, viewTermId, query };
      setViewTermId(props.terms.find((term) => dateInTerm(item.dueDate, term))?.id || "");
      setCategory(assignmentFolderId(item, folders));
      setSelected(id);
      setCreating(false);
      setEditing(undefined);
      setError("");
    },
    goBack: (onPreviousView) => {
      if (saving) return true;
      if (deleting) {
        setDeleting(undefined);
        return true;
      }
      if (copying) {
        return true;
      }
      if (folderDialog) {
        setFolderDialog(undefined);
        return true;
      }
      if (assignment) {
        const previous = directEntry.current;
        editorNavigation.current?.goBack(
          previous && onPreviousView
            ? () => {
                directEntry.current = undefined;
                setSelected(previous.selected);
                setCategory(previous.category);
                setViewTermId(previous.viewTermId);
                setQuery(previous.query);
                onPreviousView();
              }
            : undefined,
        );
        return true;
      }
      if (creating || editing) {
        closeForm();
        return true;
      }
      if (category) {
        returnToCategories();
        return true;
      }
      return false;
    },
  }));
  const classAssignments = props.assignments
    .filter((a) => a.classId === props.classId)
    .sort((a, b) => b.dueDate.localeCompare(a.dueDate));
  const orderedTerms = [...props.terms].sort((a, b) => a.startDate.localeCompare(b.startDate));
  const date = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Hong_Kong" });
  const viewTerm =
    viewTermId === ""
      ? undefined
      : orderedTerms.find((term) => term.id === viewTermId) ||
        props.recordTerm ||
        [...orderedTerms].reverse().find((term) => term.startDate <= date) ||
        orderedTerms[0];
  const schoolYears = [...new Set([...orderedTerms].reverse().map((term) => term.schoolYear))];
  const schoolYear = viewTerm?.schoolYear || schoolYears[0];
  const unassigned = classAssignments.filter((item) => !orderedTerms.some((term) => dateInTerm(item.dueDate, term)));
  const assignments = viewTerm ? classAssignments.filter((item) => dateInTerm(item.dueDate, viewTerm)) : unassigned;
  const assignment = assignments.find((a) => a.id === selected);
  const students = props.students.filter((s) => s.active).sort(compareStudentNumbers);
  const inCategory = assignments.filter((a) => assignmentFolderId(a, folders) === category);
  const matches = inCategory.filter((a) =>
    `${a.title} ${a.dueDate} ${a.note || ""}`.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const visible = matches;
  if (assignment)
    return (
      <AssignmentEditor
        key={assignment.id}
        {...props}
        students={students}
        assignment={assignment}
        navigationRef={editorNavigation}
        onBack={() => {
          directEntry.current = undefined;
          setSelected(undefined);
        }}
      />
    );

  return (
    <div className="assignment-records">
      <header className="tool-page-toolbar assignment-heading assignment-directory-heading">
        <div className="assignment-directory-title">
          <div className="assignment-title-row">
            {category && (
              <button className="assignment-back" type="button" disabled={saving} onClick={returnToCategories}>
                {i18n.t("screenCopy:assignmentRecords.backToCategories")}
              </button>
            )}
            <h2>{category ? categoryLabels[category] : copy("directoryTitle")}</h2>
          </div>
        </div>
        <div className="assignment-heading-actions">
          {!category && (
            <button
              type="button"
              ref={folderCreateButton}
              onClick={() => setFolderDialog({})}
              disabled={props.readOnly || saving || creating || !!editing}
            >
              ＋ {copy("folders.create")}
            </button>
          )}
          <button
            className="primary-action"
            disabled={props.readOnly}
            onClick={() => {
              setEditing(undefined);
              setFormDirty(false);
              setCreating(true);
            }}
          >
            {creating
              ? i18n.t("screenCopy:assignmentRecords.closeNewTask")
              : i18n.t("screenCopy:assignmentRecords.newTaskRecord")}
          </button>
        </div>
      </header>
      {notice && (
        <p role="status" className="workflow-success">
          {notice}
        </p>
      )}
      {copying && (
        <AssignmentCopyDialog
          source={copying}
          classroom={props.classroom}
          classes={props.classes}
          dateRange={props.dateRange}
          readOnly={props.readOnly}
          onClose={() => setCopying(undefined)}
          onSave={async (request) => {
            await props.onCopy(request);
            setNotice(copy("copy.success", { count: request.targets.length }));
          }}
        />
      )}
      {orderedTerms.length > 0 && (
        <div className="assignment-period">
          {schoolYears.length > 1 ? (
            <label>
              {copy("schoolYear")}
              <select
                aria-label={copy("schoolYear")}
                value={schoolYear}
                disabled={saving || creating || !!editing}
                onChange={(event) => {
                  setViewTermId([...orderedTerms].reverse().find((term) => term.schoolYear === event.target.value)?.id);
                  setQuery("");
                }}
              >
                {schoolYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <strong>{copy("schoolYearLabel", { year: schoolYear || "" })}</strong>
          )}
          <div className="assignment-term-options" role="group" aria-label={copy("viewTerm")}>
            {orderedTerms
              .filter((term) => term.schoolYear === schoolYear)
              .map((term) => (
                <button
                  type="button"
                  key={term.id}
                  aria-pressed={viewTerm?.id === term.id}
                  disabled={saving || creating || !!editing}
                  onClick={() => {
                    setViewTermId(term.id);
                    props.onSelectTerm?.(term.id);
                    setQuery("");
                  }}
                >
                  {term.name}
                </button>
              ))}
            {unassigned.length > 0 && (
              <button
                type="button"
                aria-pressed={!viewTerm}
                disabled={saving || creating || !!editing}
                onClick={() => {
                  setViewTermId("");
                  setQuery("");
                }}
              >
                {copy("unassignedTerm")}
              </button>
            )}
          </div>
        </div>
      )}
      {!category && (
        <fieldset className="assignment-folders" aria-label={i18n.t("screenCopy:assignmentRecords.recordCategories")}>
          {Object.entries(categoryLabels).map(([value, label]) => {
            const count = assignments.filter((a) => assignmentFolderId(a, folders) === value).length;
            if (value === "uncategorized" && !count) return null;
            return (
              <div className="assignment-folder-card" key={value}>
                <button
                  type="button"
                  className="paper-card assignment-folder"
                  disabled={saving}
                  aria-label={copy("folders.open", { name: label })}
                  onClick={() => {
                    setCategory(value);
                    setQuery("");
                    setCreating(false);
                    setEditing(undefined);
                    setError("");
                  }}
                >
                  <AssignmentFolderIcon />
                  <strong>{label}</strong>
                  <span className="assignment-folder-footer">
                    <span>{copy("folders.count", { count })}</span>
                    <svg className="assignment-folder-arrow" aria-hidden="true" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M4 10h11m-4-4 4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>
                {value !== "uncategorized" && (
                  <button
                    className="assignment-folder-manage"
                    type="button"
                    aria-label={copy("folders.manage", { name: label })}
                    disabled={props.readOnly || saving || creating || !!editing}
                    onClick={() => setFolderDialog({ folder: folders.find((folder) => folder.id === value) })}
                  >
                    ⋯
                  </button>
                )}
              </div>
            );
          })}
        </fieldset>
      )}
      {folderDialog && (
        <AssignmentFolderDialog
          folder={folderDialog.folder}
          count={
            classAssignments.filter((item) => assignmentFolderId(item, folders) === folderDialog.folder?.id).length
          }
          onSave={props.onFolderAction}
          onClose={() => setFolderDialog(undefined)}
          fallbackFocus={() => folderCreateButton.current}
        />
      )}
      {(creating || editing) && (
        <div
          className="assignment-drawer-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeForm();
          }}
        >
          <section
            className="assignment-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={copy(editing ? "editMetadata" : "createTitle")}
            ref={formDialogRef}
            tabIndex={-1}
          >
            <header className="assignment-drawer-heading">
              <h2>{copy(editing ? "editMetadata" : "createTitle")}</h2>
              <button type="button" aria-label={copy("close")} disabled={saving} onClick={closeForm}>
                ×
              </button>
            </header>
            <form
              key={editing?.id || "new"}
              className="assignment-create"
              onChange={() => setFormDirty(true)}
              onSubmit={async (event) => {
                event.preventDefault();
                if (saving) return;
                const data = new FormData(event.currentTarget);
                const title = String(data.get("title") || "").trim();
                const scoreText = String(data.get("maxScore") || "");
                const maxScore = scoreText ? Number(scoreText) : undefined;
                if (!title || (maxScore !== undefined && (!Number.isFinite(maxScore) || maxScore <= 0))) {
                  setError(i18n.t("screenCopy:assignmentRecords.enterATaskNameAndAValid"));
                  return;
                }
                if (
                  editing &&
                  maxScore !== undefined &&
                  props.records.some(
                    (r) =>
                      r.assignmentId === editing.id &&
                      Object.values(r.students).some((s) => s.score !== undefined && s.score > maxScore),
                  )
                ) {
                  setError(i18n.t("screenCopy:assignmentRecords.theMaximumCannotBeLowerThanSaved"));
                  return;
                }
                setSaving(true);
                setError("");
                const now = new Date().toISOString();
                const item: HomeworkAssignment = {
                  ...editing,
                  id: editing?.id || crypto.randomUUID(),
                  workspaceId: props.workspaceId,
                  classId: props.classId,
                  title,
                  dueDate: String(data.get("date")),
                  category: String(data.get("category")) as HomeworkAssignment["category"],
                  folderId: String(data.get("folder") || "uncategorized"),
                  note: String(data.get("note") || "").trim(),
                  maxScore,
                  createdAt: editing?.createdAt || now,
                  updatedAt: now,
                };
                try {
                  await props.onSaveAssignment(item);
                  setCategory(assignmentFolderId(item, folders));
                  setCreating(false);
                  setEditing(undefined);
                  setFormDirty(false);
                  if (!editing) setSelected(item.id);
                } catch {
                  setError(i18n.t("screenCopy:assignmentRecords.couldNotSaveTheTaskTryAgain"));
                } finally {
                  setSaving(false);
                }
              }}
            >
              <label>
                {i18n.t("classroomInterface:itemName")}
                <input
                  name="title"
                  required
                  maxLength={120}
                  defaultValue={editing?.title}
                  placeholder={i18n.t("screenCopy:assignmentRecords.eGWorksheetUnitTest")}
                  data-dialog-initial-focus
                />
              </label>
              <label>
                {i18n.t("screenCopy:assignmentRecords.dueTestDate")}
                <input
                  name="date"
                  min={viewTerm?.startDate}
                  max={viewTerm?.endDate}
                  type="date"
                  required
                  defaultValue={
                    editing?.dueDate ||
                    (viewTerm
                      ? date < viewTerm.startDate
                        ? viewTerm.startDate
                        : date > viewTerm.endDate
                          ? viewTerm.endDate
                          : date
                      : props.defaultRecordDate || date)
                  }
                />
              </label>
              <AssignmentClassificationFields
                folders={folders}
                defaultFolder={
                  editing
                    ? assignmentFolderId(editing, folders)
                    : category ||
                      (folders.some((folder) => folder.id === "homework")
                        ? "homework"
                        : folders[0]?.id || "uncategorized")
                }
                defaultCategory={
                  editing?.category || (category === "assessment" || category === "dictation" ? category : "homework")
                }
                onChange={() => setFormDirty(true)}
              />
              <label>
                {i18n.t("screenCopy:assignmentRecords.maximumScoreOptional")}
                <input
                  name="maxScore"
                  type="number"
                  min="0.01"
                  step="any"
                  defaultValue={editing?.maxScore}
                  placeholder={i18n.t("screenCopy:assignmentRecords.eG")}
                />
              </label>
              <label>
                {i18n.t("screenCopy:assignmentRecords.taskNotes")}
                <textarea name="note" maxLength={500} defaultValue={editing?.note} />
              </label>
              {error && (
                <p role="alert" className="form-error">
                  {error}
                </p>
              )}
              <footer className="assignment-drawer-actions">
                <button type="button" disabled={saving} onClick={closeForm}>
                  {i18n.t("personalTools:cancel")}
                </button>
                <button className="primary-action" disabled={props.readOnly || saving}>
                  {saving ? i18n.t("personalTools:saving") : copy(editing ? "saveMetadata" : "createAndRecord")}
                </button>
              </footer>
            </form>
          </section>
        </div>
      )}
      {deleting && (
        <div className="assignment-drawer-backdrop">
          <section
            className="assignment-delete-dialog paper-card"
            role="dialog"
            aria-modal="true"
            aria-label={copy("deleteTitle")}
            ref={deleteDialogRef}
            tabIndex={-1}
          >
            <h2>{copy("deleteTitle")}</h2>
            <p>
              {copy("deleteMessage", {
                title: deleting.item.title,
                count: new Set(
                  props.records
                    .filter((record) => record.assignmentId === deleting.item.id)
                    .flatMap((record) => Object.keys(record.students || {})),
                ).size,
              })}
            </p>
            <p>{copy("deleteWarning")}</p>
            {error && (
              <p role="alert" className="form-error">
                {error}
              </p>
            )}
            <div className="assignment-drawer-actions">
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setDeleting(undefined);
                  setError("");
                }}
              >
                {copy("cancel")}
              </button>
              <button
                type="button"
                className="danger-action"
                disabled={props.readOnly || saving}
                onClick={async () => {
                  if (saving) return;
                  setSaving(true);
                  setError("");
                  try {
                    await props.onDeleteAssignment(deleting.item, deleting.token);
                    setDeleting(undefined);
                  } catch (caught) {
                    setError(caught instanceof Error ? caught.message : copy("deleteFailed"));
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {copy("confirmDelete")}
              </button>
            </div>
          </section>
        </div>
      )}
      {error && !creating && !editing && !deleting && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}
      {category && (
        <>
          <label>
            {i18n.t("screenCopy:assignmentRecords.searchTasks")}
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={i18n.t("screenCopy:assignmentRecords.searchTasksDatesOrNotes")}
            />
          </label>
          <small>
            {i18n.t("screenCopy:assignmentRecords.showing")} {visible.length}／{matches.length}{" "}
            {i18n.t("screenCopy:assignmentRecords.records")}
          </small>
          <div className="assignment-directory">
            {visible.length > 0 && (
              <div className="assignment-columns" aria-hidden="true">
                <span>
                  {category === "homework" || category === "assessment" || category === "dictation"
                    ? assignmentRecordLabels(category).date
                    : copy("folders.date")}
                </span>
                <span>{i18n.t("screenCopy:assignmentRecords.taskDetails")}</span>
                <span>
                  {category === "homework" || category === "assessment" || category === "dictation"
                    ? assignmentRecordLabels(category).record
                    : copy("folders.record")}
                </span>
                <span>{i18n.t("gradeOverview:stats.progress")}</span>
                <span>{copy("actions")}</span>
              </div>
            )}
            {visible.map((a) => {
              const labels = assignmentRecordLabels(a.category);
              const record = props.records.find((r) => r.classId === props.classId && r.assignmentId === a.id);
              const missing = students.filter((s) => record?.students[s.id]?.status === "missing");
              const pending = students.filter((s) => !record?.students[s.id]);
              const count = (status: HomeworkStudentRecord["status"]) =>
                students.filter((s) => record?.students[s.id]?.status === status).length;
              const scored = students.filter((s) => record?.students[s.id]?.score !== undefined).length;
              const date = new Date(`${a.dueDate}T12:00:00`);
              return (
                <article key={a.id} className="paper-card assignment-entry">
                  <button
                    className="assignment-open"
                    aria-label={i18n.t("screenCopy:assignmentRecords.open", { title: a.title })}
                    onClick={() => setSelected(a.id)}
                  >
                    <span className="assignment-date">
                      <b>{date.getDate()}</b>
                      <small>{date.toLocaleDateString(interfaceLocale(), { month: "short", weekday: "short" })}</small>
                    </span>
                    <span className="assignment-title">
                      <b>{a.title}</b>
                      {a.note && <small>{a.note}</small>}
                    </span>
                    <span className="assignment-counts">
                      <span className="complete">
                        {count("complete")} {labels.counts.complete}
                      </span>
                      <span className="missing">
                        {missing.length} {labels.counts.missing}
                      </span>
                      <span className="late">
                        {count("late")} {labels.counts.late}
                      </span>
                      {pending.length > 0 && (
                        <small>
                          {pending.length} {i18n.t("gradeOverview:dashboard.unrecorded")}
                        </small>
                      )}
                      {count("excused") > 0 && (
                        <small>
                          {count("excused")} {labels.counts.excused}
                        </small>
                      )}
                    </span>
                    <span className="assignment-progress">
                      <span>
                        <b>{scored}</b>／{students.length}
                      </span>
                      <small>
                        {i18n.t("gradeOverview:students.scored")}
                        {a.maxScore !== undefined
                          ? i18n.t("screenCopy:assignmentRecords.outOf", { maxScore: a.maxScore })
                          : ""}
                      </small>
                    </span>
                  </button>
                  <div className="assignment-entry-actions">
                    <button
                      className="primary-action assignment-grade"
                      aria-label={copy(
                        a.category === "assessment" || a.category === "dictation"
                          ? "enterExamRecordsLabel"
                          : "enterRecordsLabel",
                        { title: a.title },
                      )}
                      onClick={() => setSelected(a.id)}
                    >
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M4 4h10v16H4zM4 9h9M9 4v16m6-9 4-4 2 2-4 4-3 1 1-3Z" />
                      </svg>
                      {copy(
                        a.category === "assessment" || a.category === "dictation" ? "enterExamRecords" : "enterRecords",
                      )}
                    </button>
                    <AssignmentActions
                      title={a.title}
                      disabled={props.readOnly || saving}
                      onCopy={() => setCopying(a)}
                      onDelete={() => {
                        setError("");
                        setDeleting({ item: a, token: props.deletionToken(a) });
                      }}
                      onEdit={() => {
                        setCreating(false);
                        setEditing(a);
                        setFormDirty(false);
                        setError("");
                      }}
                    />
                  </div>
                  <p className="assignment-missing">
                    {missing.length
                      ? labels.missingList(
                          missing
                            .map((s) => i18n.t("screenCopy:assignmentRecords.no", { number: s.number, name: s.name }))
                            .join("、"),
                        )
                      : labels.noMissing}
                  </p>
                </article>
              );
            })}
          </div>
          {!inCategory.length && <p className="paper-card empty-copy">{copy("emptyCategory")}</p>}
          {inCategory.length > 0 && !matches.length && (
            <p className="empty-copy">{i18n.t("screenCopy:assignmentRecords.noRecordsMatchYourSearch")}</p>
          )}
        </>
      )}
    </div>
  );
}

function AssignmentActions({
  title,
  disabled,
  onEdit,
  onCopy,
  onDelete,
}: {
  title: string;
  disabled?: boolean;
  onEdit: () => void;
  onCopy: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation("assignmentRecords");
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    root.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);
  function choose(action: () => void) {
    setOpen(false);
    trigger.current?.focus();
    action();
  }
  return (
    <div
      className="assignment-more"
      ref={root}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          setOpen(false);
          trigger.current?.focus();
        }
        if (open && ["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
          event.preventDefault();
          const items = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')];
          const current = items.indexOf(document.activeElement as HTMLButtonElement);
          items[
            event.key === "Home"
              ? 0
              : event.key === "End"
                ? items.length - 1
                : (current + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length
          ]?.focus();
        }
      }}
    >
      <button
        type="button"
        ref={trigger}
        className="assignment-more-trigger"
        aria-label={t("moreActions", { title })}
        aria-expanded={open}
        aria-haspopup="menu"
        disabled={disabled}
        onClick={() => setOpen(!open)}
      >
        ⋯
      </button>
      {open && (
        <div role="menu" aria-label={t("moreActions", { title })} className="assignment-more-menu">
          <button type="button" role="menuitem" onClick={() => choose(onEdit)}>
            {t("editMetadata")}
          </button>
          <button type="button" role="menuitem" onClick={() => choose(onCopy)}>
            {t("copy.open")}
          </button>
          <button type="button" role="menuitem" className="danger-menu-item" onClick={() => choose(onDelete)}>
            {t("delete")}
          </button>
        </div>
      )}
    </div>
  );
}

function AssignmentClassificationFields({
  folders,
  defaultFolder,
  defaultCategory,
  onChange,
}: {
  folders: AssignmentFolder[];
  defaultFolder: string;
  defaultCategory: NonNullable<HomeworkAssignment["category"]>;
  onChange: () => void;
}) {
  const { t } = useTranslation("assignmentRecords");
  const [folder, setFolder] = useState(defaultFolder);
  const [category, setCategory] = useState(defaultCategory);
  return (
    <>
      <div>
        <input type="hidden" name="folder" value={folder} />
        <GradeCategory
          value={folder}
          onChange={(value) => {
            setFolder(value);
            onChange();
          }}
          label={t("folders.folder")}
          options={[
            ...folders.map((item) => ({ value: item.id, label: item.name })),
            { value: "uncategorized", label: t("folders.uncategorized") },
          ]}
        />
      </div>
      <label>
        {t("folders.recordType")}
        <select
          name="category"
          required
          value={category}
          onChange={(event) => {
            const next = event.target.value as NonNullable<HomeworkAssignment["category"]>;
            setCategory(next);
            onChange();
            if (["homework", "assessment", "dictation"].includes(folder))
              setFolder(folders.some((item) => item.id === next) ? next : "uncategorized");
          }}
        >
          <option value="homework">{t("folders.defaultNames.homework")}</option>
          <option value="assessment">{t("folders.defaultNames.assessment")}</option>
          <option value="dictation">{t("folders.defaultNames.dictation")}</option>
        </select>
      </label>
    </>
  );
}

function AssignmentEditor({
  assignment,
  onBack,
  navigationRef,
  ...props
}: Props & { assignment: HomeworkAssignment; onBack: () => void }) {
  const { t } = useTranslation("keyboard");
  const { t: copy } = useTranslation("assignmentRecords");
  const labels = assignmentRecordLabels(assignment.category);
  const [entryView, setEntryView] = useNavigationState(
    `assignmentEntry:${props.classId}:view`,
    () => (window.matchMedia("(min-width: 1200px) and (pointer: fine)").matches ? "table" : "cards"),
    (value): value is "table" | "cards" => value === "table" || value === "cards",
  );
  const [batchOpen, setBatchOpen] = useState(false);
  const scoreInputs = useRef(new Map<string, HTMLInputElement>());
  const pageSize = useStudentCardPageSize();
  const pagination = useStudentPagination(props.students, JSON.stringify([props.classId, assignment.id]), pageSize);
  const existing = props.records.find((r) => r.classId === props.classId && r.assignmentId === assignment.id);
  const [rejectedScores, setRejectedScores] = useState<Record<string, boolean>>({});
  const validScore = (score?: number) =>
    score === undefined ||
    (Number.isFinite(score) && score >= 0 && (assignment.maxScore === undefined || score <= assignment.maxScore));
  const autosave = useAutosavedDraft<HomeworkRecord["students"]>({
    scope: JSON.stringify([props.workspaceId, props.classId, assignment.id]),
    source: existing?.students || {},
    writable: !props.readOnly,
    isValid: (next) => Object.values(next).every((row) => validScore(row.score)),
    onSave: (students) =>
      props.onSaveRecord({
        id: existing?.id || `assignment-${assignment.id}`,
        workspaceId: props.workspaceId,
        classId: props.classId,
        assignmentId: assignment.id,
        students,
        updatedAt: new Date().toISOString(),
      }),
  });
  const { value: draft, update: setDraft, dirty, saving } = autosave;
  const invalidScore = Object.values(draft).some((row) => !validScore(row.score));
  const scoreErrorId = `assignment-${assignment.id}-score-error`;
  const goBack = (onReturn = onBack) => {
    if (!saving && (!dirty || window.confirm(i18n.t("screenCopy:assignmentRecords.youHaveUnsavedChangesReturnToThe"))))
      onReturn();
    return true;
  };
  useImperativeHandle(navigationRef, () => ({ goBack }));
  const update = (id: string, patch: Partial<HomeworkStudentRecord>) => {
    setDraft((current) => ({ ...current, [id]: { ...(current[id] || { status: "complete" as const }), ...patch } }));
  };
  function statusField(student: Student, compact = false) {
    const row = draft[student.id];
    return (
      <label>
        <span className={compact ? "sr-only" : undefined}>{labels.state}</span>
        <select
          aria-label={labels.studentState(studentNumberLabel(student), student.name)}
          id={`assignment-${assignment.id}-${student.id}-status`}
          value={row?.status || ""}
          disabled={props.readOnly}
          onChange={(event) => update(student.id, { status: event.target.value as HomeworkStudentRecord["status"] })}
        >
          <option value="" disabled>
            {i18n.t("gradeOverview:dashboard.unrecorded")}
          </option>
          {Object.entries(labels.statuses).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
    );
  }
  function scoreField(student: Student, compact = false) {
    const row = draft[student.id];
    const scoreError = rejectedScores[student.id] || !validScore(row?.score);
    const errorId = `assignment-${assignment.id}-${student.id}-score-error`;
    return (
      <label>
        <span className={compact ? "sr-only" : undefined}>{i18n.t("screenCopy:assignmentRecords.score")}</span>
        <input
          ref={(element) => {
            if (element) scoreInputs.current.set(student.id, element);
            else scoreInputs.current.delete(student.id);
          }}
          aria-label={t("studentScore", { number: studentNumberLabel(student), name: student.name })}
          id={`assignment-${assignment.id}-${student.id}-score`}
          type="number"
          min="0"
          max={assignment.maxScore}
          step="any"
          value={row?.score ?? ""}
          disabled={props.readOnly}
          aria-invalid={scoreError || undefined}
          aria-describedby={scoreError ? errorId : undefined}
          onKeyDown={(event) => {
            if (compact && event.key === "Enter") {
              event.preventDefault();
              if (!scoreError) {
                const index = props.students.findIndex((item) => item.id === student.id);
                const next = props.students[index + (event.shiftKey ? -1 : 1)];
                if (next) {
                  scoreInputs.current.get(next.id)?.focus();
                  scoreInputs.current.get(next.id)?.select();
                }
              }
            }
          }}
          onChange={(event) => {
            const input = event.currentTarget;
            const score = input.value === "" ? undefined : input.valueAsNumber;
            const rejected = input.validity.badInput || !validScore(score);
            setRejectedScores((current) => ({ ...current, [student.id]: rejected }));
            if (rejected) {
              input.value = String(row?.score ?? "");
              return;
            }
            update(student.id, { score });
          }}
        />
        {scoreError && (
          <span id={errorId} role="alert" className="form-error assignment-score-error">
            {rejectedScores[student.id]
              ? assignment.maxScore === undefined
                ? copy("scoreRejected")
                : copy("scoreRangeRejected", { maxScore: assignment.maxScore })
              : copy("invalidScore")}
          </span>
        )}
      </label>
    );
  }
  return (
    <form
      className="assignment-records"
      aria-label={t("assignmentEditor", { title: assignment.title })}
      aria-busy={saving}
      data-autosave
      data-save-state={saving ? "saving" : dirty ? "dirty" : existing ? "saved" : "idle"}
      onSubmit={(event) => event.preventDefault()}
    >
      <header className="tool-page-toolbar assignment-heading">
        <div>
          <button type="button" disabled={saving} onClick={() => goBack()}>
            {i18n.t("screenCopy:assignmentRecords.backToRecordDirectory")}
          </button>
          <h2>{assignment.title}</h2>
          <p>
            {assignment.dueDate}
            {assignment.maxScore !== undefined
              ? i18n.t("screenCopy:assignmentRecords.outOf", { maxScore: assignment.maxScore })
              : ""}{" "}
          </p>
          <p>{assignment.note}</p>
        </div>
        <AutosaveStatus
          name={copy("studentRecords")}
          {...autosave}
          readOnly={props.readOnly}
          hasSaved={Boolean(existing)}
        />
      </header>
      {invalidScore && (
        <p id={scoreErrorId} role="alert" className="form-error">
          {copy("invalidScore")}
        </p>
      )}
      <div className="workflow-entry-toolbar">
        <div className="workflow-segments" role="group" aria-label={copy("batch.view")}>
          <button type="button" aria-pressed={entryView === "table"} onClick={() => setEntryView("table")}>
            {copy("batch.table")}
          </button>
          <button type="button" aria-pressed={entryView === "cards"} onClick={() => setEntryView("cards")}>
            {copy("batch.cards")}
          </button>
        </div>
        <button type="button" disabled={props.readOnly || !props.students.length} onClick={() => setBatchOpen(true)}>
          {copy("batch.open")}
        </button>
      </div>
      {entryView === "table" ? (
        <>
          <p className="workflow-context workflow-keyboard-hint">{copy("batch.tableHint")}</p>
          <div className="workflow-score-scroll" role="region" aria-label={copy("batch.tableLabel")}>
            <table className="workflow-score-table">
              <thead>
                <tr>
                  <th scope="col">{copy("batch.student")}</th>
                  <th scope="col">{labels.state}</th>
                  <th scope="col">{i18n.t("screenCopy:assignmentRecords.score")}</th>
                </tr>
              </thead>
              <tbody>
                {props.students.map((student) => (
                  <tr key={student.id} className={draft[student.id]?.status || "pending"}>
                    <th scope="row">
                      <span>{studentNumberLabel(student)}</span>
                      <b>{student.name}</b>
                    </th>
                    <td>{statusField(student, true)}</td>
                    <td>{scoreField(student, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div className="assignment-students">
            {pagination.items.map((student) => (
              <article
                key={student.id}
                className={`paper-card assignment-student ${draft[student.id]?.status || "pending"}`}
              >
                <h3>
                  {studentNumberLabel(student)} · {student.name}
                </h3>
                <div className="assignment-student-fields">
                  {statusField(student)}
                  {scoreField(student)}
                </div>
              </article>
            ))}
          </div>
          <StudentPagination {...pagination} disabled={props.readOnly} />
        </>
      )}
      {batchOpen && (
        <AssignmentBatchDialog
          assignment={assignment}
          students={props.students}
          draft={draft}
          readOnly={Boolean(props.readOnly)}
          onClose={() => setBatchOpen(false)}
          onApply={(patches) => setDraft((current) => ({ ...current, ...patches }))}
        />
      )}
      {!props.students.length && <p>{i18n.t("screenCopy:assignmentRecords.noActiveStudentsInThisClassImport")}</p>}
    </form>
  );
}
